import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { usersService } from "../services/usersService";
import { departmentService } from "../services/departmentService";
import ConfirmationDialog from "../components/ui/ConfirmationDialog";
import { toast } from "react-toastify";
import MultiSelect from "../utils/MultiSelect";
import { Eye, EyeOff } from "lucide-react";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { AppTable } from "../utils/AppTable";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  departments: string[];
};


const UsersList: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");


  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  // Load departments
  const loadDepartments = async () => {
    try {
      const res: any = await departmentService.list();
      const data = res?.departments || [];
      setDepartments(data.map((d: any) => ({ id: String(d.id), name: d.name })));
    } catch {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [sortBy, sortOrder, pageSize]);

  // Infinite scroll loader (GRID)
  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const res = await usersService.list({
        page,
        limit: pageSize,
        search: searchTerm,
      });

      const list = res.data?.users || res.users || [];
      const totalCount = res.total || res.data?.total || 0;
      setTotal(totalCount);

      // -------------------------
      // TABLE MODE (NO APPEND)
      // -------------------------
      if (viewMode === "list") {
        setUsers(list);        // ALWAYS replace
        setHasMore(false);     // disable infinite scroll
        return;
      }

      // -------------------------
      // GRID MODE (Infinite Scroll)
      // -------------------------
      if (page === 1) {
        setUsers(list);        // Fresh load
      } else {
        setUsers((prev) => [...prev, ...list]);  // Append
      }

      setHasMore(list.length === pageSize);

    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, sortBy, sortOrder, viewMode]);




  // Hook for infinite scroll
  const bottomRef = useInfiniteScroll(() => {
    if (viewMode === "grid" && hasMore && !loading) {
      setPage((p) => p + 1);
    }
  }, loading);

  // Reset list on search
  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [searchTerm]);

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    loadDepartments();
  }, []);

  // CREATE MODAL OPEN
  const openCreate = () => {
    setEditUser(null);
    setShowPasswordInput(true);
    reset({ firstName: "", lastName: "", email: "", password: "", departments: [] });
    setIsCreateOpen(true);
  };

  // EDIT MODAL OPEN
  const openEdit = (user: any) => {
    setEditUser(user);
    setShowPasswordInput(false);

    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: undefined,
      departments: user.departmentMemberships.map((d: any) => String(d.departmentId)),
    });

    setIsCreateOpen(true);
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    try {
      await usersService.remove(deleteId!);
      setUsers((p) => p.filter((u) => u.id !== deleteId));
      toast.success("User deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setConfirmOpen(false);
    }
  };

  // Submit create/update
  const onSubmit = async (values: FormValues) => {
    if (!values.departments.length) {
      toast.error("Please select at least one department");
      return;
    }

    setSaving(true);

    try {
      if (editUser) {
        await usersService.update(editUser.id, values);
        toast.success("User updated");
      } else {
        await usersService.create(values);
        toast.success("User created");
      }

      // setUsers([]);
      loadUsers();
      setPage(1);
      setHasMore(true);
      setIsCreateOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  // -------------------------------------------------------------------
  // UI STARTS HERE
  // -------------------------------------------------------------------

  return (
    <div className="min-h-screen">

      {/* HEADER */}
      <div className="flex justify-between p-6 bg-white shadow-sm">
        <h1 className="text-3xl font-bold">Users</h1>
        <button onClick={openCreate} className="px-4 py-2 rounded-xl text-white bg-blue-600">
          + New User
        </button>
      </div>

      {/* FILTERS */}
      <div className="max-w-6xl mx-auto p-6">
        <div className="p-4 bg-white rounded-lg flex justify-between">
          <input
            type="text"
            placeholder="Search users…"
            className="border px-3 py-2 rounded-lg w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="flex ml-4 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setViewMode("grid")}
              className={`px-4 py-2 rounded-lg ${viewMode === "grid" ? "bg-white shadow" : ""}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-4 py-2 rounded-lg ${viewMode === "list" ? "bg-white shadow" : ""}`}
            >
              List
            </button>
          </div>
        </div>

        {/* GRID VIEW */}
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {users.map((u) => (
              <div key={u.id} className="p-6 bg-white shadow rounded-xl">
                <h3 className="font-bold">{u.firstName} {u.lastName}</h3>
                <div className="mt-2 flex gap-1 flex-wrap">
                  {u.departmentMemberships.map((d: any) => (
                    <span key={d.id} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                      {d.department.name}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(u)} className="px-3 py-1 bg-yellow-500 text-white rounded">
                    Edit
                  </button>
                  <button onClick={() => confirmDelete(u.id)} className="px-3 py-1 bg-red-500 text-white rounded">
                    Delete
                  </button>
                </div>
              </div>
            ))}

            <div ref={bottomRef} className="h-10"></div>
            {loading && <p className="text-center">Loading…</p>}
          </div>
        ) : (
          <AppTable
            columns={[
              {
                key: "fullName",
                label: "Name",
                sortable: true,
                render: (u) => `${u.firstName} ${u.lastName}`,
              },
              {
                key: "departments",
                label: "Departments",
                render: (u) =>
                  u.departmentMemberships.map((d: any) => (
                    <span
                      key={d.id}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded mr-1"
                    >
                      {d.department.name}
                    </span>
                  )),
              },
              {
                key: "actions",
                label: "Actions",
                render: (u) => (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(u)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(u.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded"
                    >
                      Delete
                    </button>
                  </div>
                ),
              },
            ]}
            data={users}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onSort={(key, dir) => {
              setSortBy(key);
              setSortOrder(dir);
            }}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />

        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {isCreateOpen && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-full max-w-xl">

            <h3 className="text-xl font-semibold">
              {editUser ? "Edit User" : "Create User"}
            </h3>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">

              <input {...register("firstName", { required: true })} placeholder="First Name" className="border w-full p-2 rounded" />
              <input {...register("lastName", { required: true })} placeholder="Last Name" className="border w-full p-2 rounded" />
              <input {...register("email", { required: true })} placeholder="Email" className="border w-full p-2 rounded" />

              <MultiSelect
                label="Departments"
                options={departments}
                value={watch("departments")}
                onChange={(val) => setValue("departments", val)}
              />

              {/* PASSWORD LOGIC */}
              {editUser && !showPasswordInput && (
                <button
                  type="button"
                  onClick={() => setShowPasswordInput(true)}
                  className="px-3 py-1 bg-gray-200 rounded"
                >
                  Change Password
                </button>
              )}

              {showPasswordInput && (
                <input
                  {...register("password", { minLength: 6 })}
                  placeholder="New Password"
                  className="border w-full p-2 rounded"
                />
              )}

              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 rounded bg-gray-300" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white" type="submit">
                  Save
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doDelete}
        title="Delete User?"
        message="This action cannot be undone."
        variant="error"
      />
    </div>
  );
};

export default UsersList;
